import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCognitoAuthContext } from '@caplend/cognito-auth';
import Cognito from '../../auth/cognito';
import { oauthLogout } from '../../auth/configureAmplify';
import * as userActions from '../../store/actions/user';
import { getOnboardingStepPath, getResolvedOnboardingStepPaths, getStepFromPath, isCollectDataStepCompleted, isCompanyOnboardingUser, isCompanyPersonDataStepCompleted, isIndividualOnboardingUser, scrollOnboardingToTop } from '../../utils/onboardingUtils';
import { ChevronRightIcon } from '../../assets/icons';
import { OnboardingFooterProvider } from '../OnboardingFooterContext';
import OnboardingStepper from '../OnboardingStepper';
import OnboardingTopNav from '../OnboardingTopNav';
import HansePayLoader from '../HansePayLoader';
import { isComplianceOnboardingComplete, isFinancialOnboardingComplete } from '../../utils/financialOnboardingStorage';
import { isCompanyActivityOnboardingComplete, isCompanyRiskOnboardingComplete } from '../../utils/companyOnboardingStorage';

const ONBOARDING_PATHS_ORDERED = [
  '/verify-phone-number',
  '/choose-user-type',
  '/collect-data',
  '/collect-company-person-data',
  '/financial',
  '/compliance',
  '/accept-legal-agreements',
  '/perform-kyc',
  '/under-review',
];

const FOOTER_STEP_LABELS = {
  VerifyPhoneNumber: 'Phone Number',
  ChooseUserType: 'Type',
  CollectData: 'About you',
  Financial: 'Financial',
  Compliance: 'Compliance',
  CollectCompanyPersonData: 'Management',
  CollectCompanyOwnersData: 'Owners',
  AcceptLegalAgreements: 'Terms',
  PerformKYC: 'Verify',
  Onboarded: 'Review',
};

const COMPANY_FOOTER_STEP_LABELS = {
  Account: 'Account',
  VerifyPhoneNumber: 'Account',
  ChooseUserType: 'Account',
  CollectData: 'Company',
  CompanyDocuments: 'Documents',
  CollectCompanyPersonData: 'Management & Owners',
  CollectCompanyOwnersData: 'Management & Owners',
  Financial: 'Activity',
  Compliance: 'Activity',
  AcceptLegalAgreements: 'Terms',
  PerformKYC: 'Verify',
  Onboarded: 'Review',
};

const FOOTER_STEP_ALIASES = {
  KYC: 'PerformKYC',
  PerformKyc: 'PerformKYC',
  ApproveTermsAndConditions: 'AcceptLegalAgreements',
  TermsAndConditions: 'AcceptLegalAgreements',
};

const BASE_FOOTER_PHASES = [
  'VerifyPhoneNumber',
  'ChooseUserType',
  'CollectData',
  'Financial',
  'Compliance',
  'AcceptLegalAgreements',
  'PerformKYC',
  'Onboarded',
];

const COMPANY_FOOTER_PHASES = [
  'Account',
  'CollectData',
  'CompanyDocuments',
  'CollectCompanyPersonData',
  'Financial',
  'AcceptLegalAgreements',
  'PerformKYC',
];

function getCanonicalFooterStep(stepName) {
  if (!stepName) return null;
  return FOOTER_STEP_ALIASES[stepName] ?? stepName;
}

function mapCompanyDisplayStep(stepName) {
  const canonical = getCanonicalFooterStep(stepName);
  if (!canonical) return null;
  if (canonical === 'VerifyPhoneNumber' || canonical === 'ChooseUserType' || canonical === 'Account') return 'Account';
  if (canonical === 'CompanyDocuments') return 'CompanyDocuments';
  if (canonical === 'CollectCompanyOwnersData' || canonical === 'CollectCompanyPersonData') return 'CollectCompanyPersonData';
  if (canonical === 'Compliance' || canonical === 'Financial') return 'Financial';
  return canonical;
}

function getFooterPhases(userType, currentStep, stepsFromApi) {
  const canonicalCurrent = getCanonicalFooterStep(currentStep);
  const hasCompanyPersonStep = Array.isArray(stepsFromApi)
    ? stepsFromApi.some((step) => getCanonicalFooterStep(typeof step === 'string' ? step : step?.step) === 'CollectCompanyPersonData')
    : false;
  const shouldIncludeCompanySteps =
    userType === 'Company' ||
    canonicalCurrent === 'CollectCompanyPersonData' ||
    canonicalCurrent === 'CompanyDocuments' ||
    hasCompanyPersonStep;

  if (!shouldIncludeCompanySteps) return BASE_FOOTER_PHASES;

  return COMPANY_FOOTER_PHASES;
}

function getFooterStepLabel(stepName, userType) {
  const labels = isCompanyOnboardingUser(userType) ? COMPANY_FOOTER_STEP_LABELS : FOOTER_STEP_LABELS;
  return labels[stepName] ?? stepName ?? '';
}

function OnboardingOutlet() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { setHasAccessToken } = useCognitoAuthContext();
  const { user, onboardingStatus, gettingOnboardingStatus, gettingUser } = useSelector((state) => state.user);
  const hasFetchedRef = useRef(false);
  const [footerConfig, setFooterConfig] = useState({});

  const isUserOnboarding = user?.isOnboarding;
  const currentStep = onboardingStatus?.currentStep;
  const userStepsCompleted = onboardingStatus?.userStepsCompleted === true;
  const currentPath = location.pathname;
  const steps = onboardingStatus?.steps || [];
  const shouldIncludeFinancialStep = isIndividualOnboardingUser(user) && isCollectDataStepCompleted(steps);
  const shouldIncludeComplianceStep = shouldIncludeFinancialStep;
  const shouldIncludeCompanyActivityStep = isCompanyOnboardingUser(user) && isCompanyPersonDataStepCompleted(steps);
  const shouldIncludeCompanyRiskStep = shouldIncludeCompanyActivityStep;

  const validStepPaths = getResolvedOnboardingStepPaths(steps, { userType: user?.userType });

  const isUnderReviewPath = currentPath === '/under-review';
  const cameBackFromUnderReview = location.state?.fromUnderReviewBack === true;

  const stepPath = getOnboardingStepPath(currentStep);
  const fallbackRedirectPath = stepPath;

  const orderedIndex = ONBOARDING_PATHS_ORDERED.indexOf(currentPath);
  const currentStepOrderedIndex = stepPath ? ONBOARDING_PATHS_ORDERED.indexOf(stepPath) : -1;

  const isPreviousOrCurrentOnboardingPath = orderedIndex >= 0 && currentStepOrderedIndex >= 0 && orderedIndex <= currentStepOrderedIndex;

  const isCompanyPersonDataPath = currentPath === '/collect-company-person-data';
  const isCompanyPersonDataAllowed = validStepPaths.includes(currentPath);
  const isPreviousOrCurrentOnboardingPathAllowed = isPreviousOrCurrentOnboardingPath && (!isCompanyPersonDataPath || isCompanyPersonDataAllowed);

  const isOnValidStepPath =
    validStepPaths.includes(currentPath) ||
    (isUnderReviewPath && userStepsCompleted) ||
    (cameBackFromUnderReview && ONBOARDING_PATHS_ORDERED.includes(currentPath)) ||
    isPreviousOrCurrentOnboardingPathAllowed;

  let pathStepName = getStepFromPath(currentPath, steps);
  if (currentPath === '/financial') {
    pathStepName = 'Financial';
  } else if (currentPath === '/compliance') {
    pathStepName = 'Compliance';
  }
  const companyCollectSubstep = location.state?.companyCollectSubstep;
  const isCompanyDocumentsSubscreen =
    isCompanyOnboardingUser(user) &&
    currentPath === '/collect-data' &&
    (companyCollectSubstep === 'documents' || companyCollectSubstep === 'review');

  let stepFromPathFallback = null;
  if (isUnderReviewPath) {
    stepFromPathFallback = 'Onboarded';
  } else if (currentPath === '/perform-kyc') {
    stepFromPathFallback = 'PerformKYC';
  }

  const rawDisplayCurrentStep = isCompanyDocumentsSubscreen
    ? 'CompanyDocuments'
    : (pathStepName ?? stepFromPathFallback ?? currentStep);
  const displayCurrentStep = isCompanyOnboardingUser(user)
    ? mapCompanyDisplayStep(rawDisplayCurrentStep)
    : rawDisplayCurrentStep;

  const shouldRedirectToUnderReview = userStepsCompleted && !isUnderReviewPath && !cameBackFromUnderReview;
  const shouldRedirectToFinancial =
    ((shouldIncludeFinancialStep && !isFinancialOnboardingComplete(user)) ||
      (shouldIncludeCompanyActivityStep && !isCompanyActivityOnboardingComplete(user))) &&
    currentStep === 'AcceptLegalAgreements' &&
    currentPath === '/accept-legal-agreements';
  const shouldRedirectToCompliance =
    ((shouldIncludeComplianceStep && isFinancialOnboardingComplete(user) && !isComplianceOnboardingComplete(user)) ||
      (shouldIncludeCompanyRiskStep &&
        isCompanyActivityOnboardingComplete(user) &&
        !isCompanyRiskOnboardingComplete(user))) &&
    currentStep === 'AcceptLegalAgreements' &&
    currentPath === '/accept-legal-agreements' &&
    !gettingUser;
  const currentValidStepIndex = validStepPaths.indexOf(currentPath);
  const previousStepPathForBack = currentValidStepIndex > 0 ? validStepPaths[currentValidStepIndex - 1] : null;
  const nextStepPath =
    currentValidStepIndex >= 0 && currentValidStepIndex < validStepPaths.length - 1 ? validStepPaths[currentValidStepIndex + 1] : null;
  const canonicalFooterStep = getCanonicalFooterStep(displayCurrentStep);
  const footerPhases = getFooterPhases(user?.userType, canonicalFooterStep, steps);
  const currentFooterStepIndex = footerPhases.indexOf(canonicalFooterStep);
  const footerStepCount = footerPhases.length;
  const footerStepNumber = currentFooterStepIndex >= 0 ? currentFooterStepIndex + 1 : 1;
  const footerPhaseLabel = footerConfig.phaseLabel ?? getFooterStepLabel(canonicalFooterStep, user?.userType);
  const footerContinueLabel = footerConfig.continueLabel ?? 'Continue';
  const footerBackLabel = footerConfig.backLabel ?? 'Back';
  const hasFooterBackAction = Boolean(footerConfig.onBack || previousStepPathForBack);
  const isFinalReviewPath = currentPath === '/under-review';
  const showOnboardingStepper = !isFinalReviewPath;
  const showOnboardingFooter = isOnValidStepPath && !isFinalReviewPath && footerConfig.hidden !== true;
  const outletLayoutClass = showOnboardingFooter ? 'pb-24 text-[#0B1929] sm:min-h-screen' : 'text-[#0B1929]';
  const outletContentClass = isFinalReviewPath ? 'relative' : 'relative sm:min-h-[60vh]';
  const isCurrentPathStepCompleted =
    pathStepName != null &&
    steps.some((s) => (typeof s === 'string' ? s : s?.step) === pathStepName && typeof s === 'object' && s?.isCompleted === true);

  const footerContextValue = useMemo(
    () => ({
      setFooterConfig,
    }),
    [],
  );

  useEffect(() => {
    if (isUserOnboarding && !onboardingStatus && !gettingOnboardingStatus && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(userActions.getOnboardingStatus());
    }

    if (!isUserOnboarding) {
      hasFetchedRef.current = false;
    }
  }, [isUserOnboarding, onboardingStatus, gettingOnboardingStatus, dispatch]);

  const companyPersonsScreen = location.state?.companyPersonsScreen;

  useEffect(() => {
    scrollOnboardingToTop();
  }, [currentPath, companyPersonsScreen, companyCollectSubstep]);

  const handleLogout = () => {
    if (process.env.REACT_APP_COGNITO_DOMAIN) {
      oauthLogout();
      return;
    }
    setHasAccessToken(false);
    Cognito.logOut();
  };

  if (!user) {
    if (gettingUser) {
      return <HansePayLoader variant='splash' />;
    }
    return null;
  }
  if (!isUserOnboarding) {
    if (ONBOARDING_PATHS_ORDERED.includes(currentPath)) {
      return <Navigate to='/dashboard' replace />;
    }
    return <Outlet />;
  }
  if (gettingOnboardingStatus || !onboardingStatus) return null;
  if (shouldRedirectToUnderReview) return <Navigate to='/under-review' replace />;
  if (shouldRedirectToFinancial) return <Navigate to='/financial' replace />;
  if (shouldRedirectToCompliance) return <Navigate to='/compliance' replace />;

  const handleFooterBack = () => {
    scrollOnboardingToTop();
    if (footerConfig.onBack) {
      footerConfig.onBack();
      return;
    }
    if (previousStepPathForBack) {
      const state = cameBackFromUnderReview ? { fromUnderReviewBack: true } : undefined;
      navigate(previousStepPathForBack, { state, replace: false });
    }
  };

  const handleFooterContinue = () => {
    scrollOnboardingToTop();
    if (footerConfig.onContinue) {
      footerConfig.onContinue();
      return;
    }
    if (currentPath === '/financial' || currentPath === '/compliance') {
      return;
    }
    if (nextStepPath) {
      const state = cameBackFromUnderReview ? { fromUnderReviewBack: true } : undefined;
      navigate(nextStepPath, { state, replace: false });
    }
  };

  return (
    <div className={outletLayoutClass}>
      <OnboardingFooterProvider value={footerContextValue}>
        <OnboardingTopNav onExit={handleLogout} />
        {showOnboardingStepper && (
          <OnboardingStepper onboardingStatus={onboardingStatus} displayCurrentStep={displayCurrentStep} userType={user?.userType} />
        )}

        {isOnValidStepPath && (
          <div className={outletContentClass}>
            <Outlet />
          </div>
        )}

        {showOnboardingFooter && (
          <div className='fixed bottom-0 left-0 right-0 z-50 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 border-t border-[#0B1929]/[0.07] bg-white/95 px-3 py-3 backdrop-blur-md sm:flex sm:gap-4 sm:px-10 sm:py-3.5'>
            <div className='flex min-w-0 flex-1 justify-start'>
              {hasFooterBackAction && (
                <button
                  type='button'
                  onClick={handleFooterBack}
                  disabled={
                    footerConfig.onBack
                      ? Boolean(footerConfig.backDisabled)
                      : (footerConfig.backDisabled ?? !previousStepPathForBack)
                  }
                  aria-label={footerBackLabel}
                  className='inline-flex h-11 w-11 max-w-full items-center justify-center rounded-full border border-[#0B1929]/15 bg-white text-[12px] font-medium text-[#3D5A73] transition hover:bg-[#EBF4FB] disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:w-auto sm:gap-1.5 sm:px-4 sm:text-[13px]'
                >
                  <ChevronRightIcon className='h-4 w-4 shrink-0 rotate-180' />
                  <span className='hidden truncate sm:inline'>{footerBackLabel}</span>
                </button>
              )}
            </div>
            <div className='min-w-0 whitespace-nowrap text-center text-[11px] text-[#7A9AB0] sm:text-[12.5px]'>
              <b className='inline-block max-w-[88px] truncate align-bottom font-semibold text-[#0F2540] sm:max-w-none'>{footerPhaseLabel}</b>
              <span className='mx-2 text-[#C4DDEF]'> </span>
              <span className='hidden min-[360px]:inline'>
                Step {footerStepNumber} of {footerStepCount}
              </span>
              <span className='min-[360px]:hidden'>
                {footerStepNumber}/{footerStepCount}
              </span>
            </div>
            <div className='flex min-w-0 flex-1 justify-end'>
              <button
                type='button'
                onClick={handleFooterContinue}
                disabled={footerConfig.continueDisabled ?? (!footerConfig.onContinue && (!nextStepPath || !isCurrentPathStepCompleted))}
                aria-label={footerContinueLabel}
                className='inline-flex h-11 w-11 max-w-full items-center justify-center rounded-full bg-[#0B1929] text-[13px] font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9] sm:w-auto sm:px-7 sm:text-sm'
              >
                <span className='hidden truncate sm:inline'>{footerContinueLabel}</span>
                <ChevronRightIcon className='h-4 w-4 shrink-0 sm:ml-1.5' />
              </button>
            </div>
          </div>
        )}

        {!isOnValidStepPath && fallbackRedirectPath && <Navigate to={fallbackRedirectPath} replace />}
        {!isOnValidStepPath && !fallbackRedirectPath && <Navigate to='/verify-phone-number' replace />}
      </OnboardingFooterProvider>
    </div>
  );
}

export default OnboardingOutlet;
